import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { IconService } from "@app/core/services/icon.service";
import { DesignService } from "@app/core/services/design.service";

@Component({
  selector: "app-icon",
  templateUrl: "./app-icon.component.html",
  styleUrls: ["./app-icon.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppIconComponent implements OnChanges {
  // FontAwesome icon array, e.g. ['fas','times'] (used only for classic design)
  @Input() icon: string[] = [];
  // Icon name as path parts for modern design e.g. ['folder','name'] -> /assets/design-modern/icons/folder/name.svg
  @Input() iconName: string[] = [];
  /**
   * If true, force using the modern icon storage (SVG from `iconName` or `icon`) regardless
   * of the currently active design. The component will attempt to load and inline the SVG;
   * if loading fails it will fall back to the classic FontAwesome rendering.
   */
  @Input() isStrictModern?: boolean;
  @Input() size?: number | string; // px or string (e.g. '24')
  @Input() color?: string; // used for modern SVGs
  @Input() alt = "";
  @Input() className = "";
  @Input() title = "";
  // When true, color only strokes and keep fills transparent
  @Input() strokeOnly: boolean = true;

  showClassic = true;
  svgHtmlSafe: SafeHtml | null = null;
  private lastUrl: string | null = null;
  private baseSvgText: string | null = null;

  constructor(
    private iconService: IconService,
    private design: DesignService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // Re-render only when the SVG source or structure changes; color is applied via CSS currentColor
    if (
      changes["icon"] ||
      changes["iconName"] ||
      changes["size"] ||
      changes["isStrictModern"] ||
      changes["strokeOnly"]
    ) {
      this.updateRender();
    }
  }

  private updateRender() {
    const design = this.design.getDesign();
    const useModern = this.isStrictModern === true || design === "modern";
    if (!useModern) {
      this.showClassic = true;
      this.svgHtmlSafe = null;
      this.cdr.markForCheck();
      return;
    }

    // modern design (or forced modern): load SVG and inline it so we can color it
    this.showClassic = false;
    const url =
      this.iconName && this.iconName.length > 0
        ? this.iconService.getIconUrlParts(this.iconName)
        : (() => {
            const name = this.extractNameFromIcon();
            return name ? this.iconService.getIconUrl(name) : "";
          })();

    if (!url) {
      this.showClassic = true;
      this.cdr.markForCheck();
      return;
    }
    // If URL hasn't changed and we already have the base SVG, just recolor without refetching
    if (this.lastUrl === url && this.baseSvgText) {
      this.processSvgAndSet(this.baseSvgText);
      return;
    }

    // If already in cache (preloaded), process synchronously for first paint
    const cachedNow = this.iconService.getCachedSvg(url);
    if (cachedNow) {
      this.lastUrl = url;
      this.baseSvgText = cachedNow;
      this.processSvgAndSet(cachedNow);
      return;
    }

    // Fetch only when needed; keep previous svgHtml until new one is ready to avoid flicker
    this.iconService
      .getSvg(url)
      .then((svgText) => {
        this.lastUrl = url;
        this.baseSvgText = svgText;
        this.processSvgAndSet(svgText);
      })
      .catch(() => {
        // fallback to classic FontAwesome for resilience
        this.showClassic = true;
        this.svgHtmlSafe = null;
        this.cdr.markForCheck();
      });
  }

  private extractNameFromIcon(): string | null {
    if (!this.icon || this.icon.length === 0) return null;
    return String(this.icon[this.icon.length - 1]);
  }

  private processSvgAndSet(svgText: string) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(svgText, "image/svg+xml");
      const svg = doc.querySelector("svg");
      if (!svg) throw new Error("no svg");

      // set width/height if size provided
      if (this.size) {
        const s = String(this.size);
        svg.setAttribute("width", s);
        svg.setAttribute("height", s);
      }

      // Normalize to use CSS currentColor to avoid reprocessing on color changes
      if (this.strokeOnly) {
        svg.setAttribute("stroke", "currentColor");
        svg.style.stroke = "currentColor";
        svg.setAttribute("fill", "none");
        const els = svg.querySelectorAll("*");
        els.forEach((el) => {
          try {
            const e = el as Element;
            const inDefs = !!e.closest("defs");
            const inMask = !!e.closest("mask");
            if (inDefs || inMask) return;
            if (e.hasAttribute("fill")) e.setAttribute("fill", "none");
            if (e.hasAttribute("stroke"))
              e.setAttribute("stroke", "currentColor");
          } catch {}
        });
      } else {
        const els = svg.querySelectorAll("*");
        els.forEach((el) => {
          try {
            const e = el as Element;
            if (e.hasAttribute("fill")) e.setAttribute("fill", "currentColor");
            if (e.hasAttribute("stroke"))
              e.setAttribute("stroke", "currentColor");
          } catch {}
        });
        svg.setAttribute("fill", "currentColor");
        svg.setAttribute("stroke", "currentColor");
      }

      const serialized = new XMLSerializer().serializeToString(svg);
      this.svgHtmlSafe = this.sanitizer.bypassSecurityTrustHtml(serialized);
      this.cdr.markForCheck();
    } catch (e) {
      this.showClassic = true;
      this.svgHtmlSafe = null;
      this.cdr.markForCheck();
    }
  }
}
